import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

const CLIENT_ID =
  "265011262103-v7f3hb11sfeosvvgd7l64san9702jaes.apps.googleusercontent.com";
const SCOPES =
  "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events";

export const SendInviteModal = ({
  demos = [],
  onClose,
  onSubmit,
  isLoading,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const tokenClientRef = useRef(null);
  const accessTokenRef = useRef(null);

  // Default Recipients (To filled from demos)
  const [recipients, setRecipients] = useState({
    to: demos.map((d) => d.email).filter(Boolean) || [],
    cc: [],
    bcc: [],
  });

  // Sender (Created By)
  const createdByEmails = useMemo(() => {
    if (!demos || demos.length === 0) return [];
    return demos.map((demo) => demo?.createdBy?.email).filter(Boolean);
  }, [demos]);
  
  // ✅ Load Google Identity Script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    script.onload = () => console.log("✅");
  }, []);

  // ✅ Token Handling
  const getAccessToken = () => {
    return new Promise((resolve, reject) => {
      if (!tokenClientRef.current) {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES,
          callback: (resp) => {
            if (resp.error) reject(resp.error);
            else {
              accessTokenRef.current = resp.access_token;
              resolve(resp.access_token);
            }
          },
        });
      }
      tokenClientRef.current.requestAccessToken();
    });
  };

  // ✅ Generate Meet Link
  const generateMeetLink = async () => {
    try {
      const token = accessTokenRef.current || (await getAccessToken());
      const event = {
        summary: "Demo Meeting",
        description: "Meeting created via Google Calendar API",
        start: {
          dateTime: new Date().toISOString(),
          timeZone: "Asia/Kolkata",
        },
        end: {
          dateTime: new Date(
            new Date().getTime() + 60 * 60 * 1000
          ).toISOString(),
          timeZone: "Asia/Kolkata",
        },
        conferenceData: {
          createRequest: {
            requestId: "req-" + Date.now(),
            conferenceSolutionKey: { type: "hangoutsMeet" },
          },
        },
      };

      const res = await fetch(
        "https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(event),
        }
      );

      const data = await res.json();
      if (!data.hangoutLink) throw new Error("No Meet link returned");

      setValue("meetingLink", data.hangoutLink);
      // alert("✅ Google Meet Link Generated!");
    } catch (err) {
      console.error("❌ Error generating Meet link:", err);
      alert("Failed to generate Google Meet link. Please sign in again.");
    }
  };

  // ✅ Email Input Component (Manual)
  const EmailInput = ({ label, type }) => {
    const [emailInput, setEmailInput] = useState("");

    const addEmails = (inputValue) => {
      const emails = inputValue
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);

      let validEmails = [];
      for (let email of emails) {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValid) {
          alert(`Invalid email format: ${email}`);
          continue;
        }
        const alreadyExists = Object.values(recipients).flat().includes(email);
        if (alreadyExists) {
          alert(`Email already exists: ${email}`);
          continue;
        }
        validEmails.push(email);
      }

      if (validEmails.length > 0) {
        setRecipients((prev) => ({
          ...prev,
          [type]: [...prev[type], ...validEmails],
        }));
        setEmailInput("");
      }
    };

    const removeRecipient = (email) => {
      setRecipients((prev) => ({
        ...prev,
        [type]: prev[type].filter((e) => e !== email),
      }));
    };

    return (
      <div className="text-sm">
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
    
      {/* Email chips */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {recipients[type].map((email) => (
          <span
            key={email}
            className="bg-blue-100 text-[#2C4E86] px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1"
          >
            {email}
            <button
              type="button"
              onClick={() => removeRecipient(email)}
              className="text-red-500 hover:text-red-700 leading-none"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    
      {/* Input and Add button */}
      <div className="flex gap-1.5">
        <input
          type="text"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // Prevent form submission
              addEmails(emailInput);
            }
          }}
          placeholder={`Enter ${label.toLowerCase()} email(s)`}
          className="flex-1 border rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2C4E86] placeholder:text-gray-400"
        />
        <button
          type="button"
          onClick={() => addEmails(emailInput)}
          className="bg-[#2C4E86] text-white px-3 py-1.5 rounded-md text-xs hover:bg-blue-900 transition"
        >
          Add
        </button>
      </div>
    </div>
    
    );
    
  };

  const handleFormSubmit = (data) => {
    const parsedData = {
      sender: createdByEmails[0] || "",
      to: recipients.to || [],
      cc: recipients.cc || [],
      bcc: recipients.bcc || [],
      meetingLink: data.meetingLink,
    };
  
    onSubmit(parsedData); 
  };
    

  if (demos.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-center items-center p-3">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md  p-4 transition-all scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
      
      {/* Header */}
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-gray-800">Send Meeting Invitation</h2>
        <p className="text-xs text-gray-600 mt-1">
          This will send an email with the meeting link to the following {demos.length} contact(s):
        </p>
      </div>
  
      {/* Form */}
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3 text-sm">
        {/* Manual Email Inputs */}
        <EmailInput label="To" type="to" />
        <EmailInput label="CC" type="cc" />
        <EmailInput label="BCC" type="bcc" />
  
        {/* Meeting Link Field */}
        <div>
          <label
            htmlFor="meetingLink"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Meeting Link
          </label>
          <div className="flex gap-2">
            <input
              id="meetingLink"
              {...register("meetingLink", { required: "Meeting link is required" })}
              placeholder="https://meet.google.com/..."
              defaultValue={demos?.[0]?.meetingLink || ""}
              className="flex-1 border rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={generateMeetLink}
              className="px-2 py-1.5 bg-[#2C4E86] text-white rounded text-xs hover:bg-blue-900"
            >
              Generate
            </button>
          </div>
          {errors.meetingLink && (
            <p className="text-red-500 text-[11px] mt-1">{errors.meetingLink.message}</p>
          )}
        </div>
  
        {/* Footer Buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t mt-1 sticky bottom-0 bg-white pb-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded text-xs hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-3 py-1.5 bg-[#2C4E86] text-white rounded text-xs hover:bg-blue-900 disabled:bg-blue-300"
          >
            {isLoading ? "Sending..." : "Send Invites"}
          </button>
        </div>
      </form>
    </div>
  </div>
  
  );
};
